import {
  find,
  get,
  isArray,
  isBoolean,
  isEmpty,
  isNaN,
  isNumber,
  isString,
  size,
} from 'lodash'
import moment, { Moment } from 'moment'
import postalCodes from 'postal-codes-js'
import isEmail from 'validator/lib/isEmail'

import { TDateRange } from '../../DatePicker/types'
import { TValue } from '../types'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const ibantools = require('ibantools')

type TAllValues = {
  [key: string]: TValue
}

type TReturnValidation = string | boolean | undefined | null

export type TValidator = (
  value: TValue,
  allValues: TAllValues,
) => TReturnValidation

export type TSimpleValidate = (value: string | null) => string | null

export function SimpleValidate(value: string | null): string | null {
  return value
}

export const composeValidators =
  (...validators: (TValidator | undefined)[]): any =>
  (value: TValue, allValues: TAllValues) =>
    validators
      .filter(Boolean)
      .reduce(
        (error, validator: any) => error || validator(value, allValues),
        undefined,
      )

export const mustBePhoneNumber = (value: TValue): string | undefined => {
  return !value ||
    isEmpty(value) ||
    (isString(value) && !lengthRange(4, 15)(value))
    ? undefined
    : i18n.t(
        'validator_must_be_phone_number',
        'Bitte eine gültige Telefonnummer angeben',
      )
}

export const required = (value: TValue): string | undefined => {
  if (isArray(value)) {
    return size(value) > 0
      ? undefined
      : i18n.t(
          'please_choose_min_one_option',
          'Bitte wählen sie mindestens eine Option',
        )
  }
  return ((value || value === 0) && value !== '') || isArray(value)
    ? undefined
    : `${i18n.t('is_required', 'Wird benötigt')}`
}

export const mustBeFactor = (value: TValue): string | undefined =>
  !isNaN(value as number) && value >= 0 && value <= 1
    ? undefined
    : i18n.t('enter_number_fraction_validator', `Bitte als Faktor eingeben`)

export const mustBeFactorAllowNegative = (value: TValue): string | undefined =>
  !isNaN(value as number) && Math.abs(value) <= 1
    ? undefined
    : i18n.t(
        'enter_number_fraction_with_negative_validator',
        `Bitte als positiven oder negativen Faktor eingeben`,
      )

export const mustBeSamePassword = (
  value: TValue,
  allValues: TAllValues,
): string | undefined => {
  if (isEmpty(value)) {
    return undefined
  }
  return allValues && value === allValues.password
    ? undefined
    : i18n.t(
        'password_must_match_validation',
        'Passwörter stimmen nicht überein',
      )
}

export const mustBeEmail = (value: TValue): string | undefined => {
  return (value && isEmail(String(value))) || !value || value === ''
    ? undefined
    : i18n.t(
        'validator_valid_email',
        'Bitte eine gültige E-Mail-Adresse angeben',
      )
}

export const mustbeAfterStartOrUntilCancellation = (
  value: TValue,
  allValues: TAllValues,
): string | undefined => {
  return moment(value).isAfter(moment(allValues.validFrom)) ||
    allValues.validUntilCancellation === true
    ? undefined
    : i18n.t(
        'date_must_be_later_than_start_validation',
        `Bitte wählen Sie ein Datum, dass nach dem Beginn des Starts liegt`,
      )
}

export const street_mustNotBeANumber = (value: TValue): string | undefined => {
  return isNaN(value as number)
    ? undefined
    : i18n.t(
        `validation_valid_street_name`,
        `Bitte geben Sie einen gültigen Straßennamen ein`,
      )
}

export const street_number_mustBeANumber = (
  value: TValue,
): string | undefined => {
  return !value || !isNaN(value as number)
    ? undefined
    : i18n.t(
        'enter_valid_street_no',
        `Bitte geben Sie einen gültige Hausnummer ein. Verwenden Sie für Zusätze das nächste Feld`,
      )
}

export const mustBeValidCallable = (value: TValue): string | undefined => {
  if (!value) {
    return
  }
  const regex = `^\\+?\\d+$`
  return (value as string).match(regex)
    ? undefined
    : i18n.t(
        'base_phone_validator',
        `Bitte geben Sie eine gültige Nummer an. Ihre Nummer darf mit einem "+" starten`,
      )
}

export const minLength =
  (length: number) =>
  (value: TValue): string | undefined => {
    if (String(value).length < length) {
      return i18n.t('min_length_string_validatio', {
        defaultValue: 'Mind. {{placeholder1}} Zeichen',
        placeholder1: length,
      })
    }
  }

export const lengthRange =
  (min: number, max: number) =>
  (value: TValue): string | undefined => {
    if (String(value).length > max || String(value).length < min) {
      return i18n.t('length_validator_fixed', {
        defaultValue: `Zwischen {{placeholder1}} und {{placeholder2}}  Zeichen`,
        placeholder1: min,
        placeholder2: max,
      })
    }
  }

type TOption = {
  value: TValue
  label?: string
}

export const requiredWithPresentOption =
  (options: TOption[]) =>
  (value: TValue): string | undefined => {
    return find(
      options,
      (opt) =>
        opt.value === value ||
        opt === value ||
        Boolean(
          find(
            get(opt, 'options'),
            (o: any) => o.value === value || o === value,
          ),
        ),
    )
      ? undefined
      : i18n.t('select_options_missing', 'Bitte wählen Sie eine Option')
  }

export const exactLength =
  (length: number) =>
  (value: TValue): string | undefined => {
    if (String(value).length != length) {
      return i18n.t('exact_length_validator', {
        defaultValue: `Bitte {{placeholder1}} Zeichen angeben`,
        placeholder1: length,
      })
    }
  }

export const nowOrPast = (timestamp: Moment): string | undefined => {
  if (timestamp && !timestamp.isSameOrBefore(moment())) {
    return i18n.t(
      'no_future_date_validation',
      `Darf nicht in der Zukunft liegen`,
    )
  }
}

export const mustBeNumber = (value: TValue): string | undefined => {
  return !isNaN(value as number)
    ? undefined
    : i18n.t(
        'number_number_validator',
        `Bitte geben Sie eine Zahl ein. Nutzen sie "." zur Dezimaltrennung.`,
      )
}

export const mustBeNumberUpdate = (value: TValue): string | undefined => {
  return !isNaN(value as number)
    ? undefined
    : i18n.t('enter_number_updated', `Bitte geben Sie eine Zahl ein.`)
}

export const mustNotBeDezimalNumber = (value: TValue): string | undefined => {
  return (value as number) % 1 === 0
    ? undefined
    : i18n.t('enter_whole_number', `Bitte geben Sie eine ganze Zahl `)
}

export const maxValue =
  (max: number) =>
  (value: TValue): string | undefined => {
    return value <= max
      ? undefined
      : i18n.t('maximum_allowed_value_is_x', {
          defaultValue: `Der maximal erlaubte Wert beträgt {{max}}`,
          max,
        })
  }

export const minValue =
  (minValue: number) =>
  (value: TValue): string | undefined => {
    return value >= minValue
      ? undefined
      : i18n.t('minimal_value_validator', {
          defaultValue: `Mindestens {{placeholder1}}`,
          placeholder1: minValue,
        })
  }

export const mustbeIBAN = (value: TValue): string | undefined =>
  ibantools.isValidIBAN((value as string).replace(/\s/g, ''))
    ? undefined
    : i18n.t('enter_valid_iban', `Bitte gültige IBAN eingeben`)

export const mustBeOptionalIban = (value: TValue): string | undefined => {
  if (!(value || '').trim()) {
    return undefined
  }

  return ibantools.isValidIBAN((value as string).replace(/\s/g, ''))
    ? undefined
    : i18n.t('enter_valid_iban', `Bitte gültige IBAN eingeben`)
}

export const valuesMustSolveToZero = (
  value: unknown,
  allValues: TAllValues,
): string | undefined => {
  let sum = 0

  allValues.creditorResultOverview.forEach((creditor) => {
    sum += creditor.value
  })
  allValues.debitorResultOverview.forEach((debitor) => {
    sum += debitor.value
  })

  return sum === 0
    ? undefined
    : i18n.t(
        'cred_deb_zero_validator',
        `Alle Kreditoren - und Debitorenwerte müssen sich gegenseitig ausgleichen`,
      )
}

export const mustBePositive = (value: TValue): string | undefined =>
  value && isNumber(parseFloat(value as string)) && value > 0
    ? undefined
    : i18n.t(
        'enter_positiv_number_validator',
        `Bitte geben Sie eine positive Zahl ein`,
      )

export const mustBePositiveOrZero = (value: TValue): string | undefined => {
  return !isNaN(value as number) && value >= 0
    ? undefined
    : i18n.t(
        'enter_positive_number_or_zero',
        `Bitte geben Sie eine positive Zahl oder 0 ein`,
      )
}

export const mustBePositiveOrEmpty = (value: TValue): string | undefined => {
  return !value ||
    value === '' ||
    (isNumber(parseFloat(value as string)) && value >= 0)
    ? undefined
    : i18n.t(
        'positive_or_empty_validator',
        `Bitte geben Sie eine positive Zahl oder lassen Sie das Feld leer`,
      )
}

export const mustBeValidCountryZip =
  (country: string | null | undefined) =>
  (value: TValue): string | undefined => {
    if (!country || !value || typeof country !== 'string') return undefined

    if (
      postalCodes.validate(`${country.toLowerCase()}`, value as string) !== true
    )
      return i18n.t('zip_validation_error', {
        defaultValue: `"{{placeholder1}}" ist kein gültiger ZIP Code für "{{placeholder2}}"`,
        placeholder1: value,
        placeholder2: country,
      })
  }

export const validateLat = (value: TValue): string | undefined => {
  const float = parseFloat(value)
  if (isNaN(float) || !(isFinite(float) && Math.abs(float) <= 90)) {
    return i18n.t(
      `lat_validation`,
      `Der Breitengrad muss zwischen -90 und 90 liegen`,
    )
  }
}

export const validateLon = (value: TValue): string | undefined => {
  const float = parseFloat(value)
  if (isNaN(float) || !(isFinite(float) && Math.abs(float) <= 180)) {
    return i18n.t(
      `lat_validation`,
      `Der Längengrad muss zwischen -180 und 180 liegen`,
    )
  }
}

export const mustBeBool = (value: TValue): string | null =>
  isBoolean(value) ? null : i18n.t('please_select_bool', 'Bitte wählen')

export const mustBeAllUpperCase = (value: TValue): string | undefined => {
  if (!isEmpty(value) && (value as string).toUpperCase() !== value) {
    return i18n.t(`must_be_all_upper`, `Es sind nur Großbuchstaben erlaubt`)
  }
}

export const mustBePositiveRealNumber = (value: TValue): string | undefined => {
  return value >= 0 && (value as number) % 1 === 0
    ? undefined
    : i18n.t(
        'positive_number_or_zero',
        'Bitte geben Sie eine natürliche Zahl an',
      )
}

export const mustBeFloat = (value: string): string | undefined => {
  return !isNaN(parseFloat(value)) || !value
    ? undefined
    : i18n.t('invalid_value', 'Ungültiger Wert')
}

export const mustBeValidDaterange = (value: TDateRange): string | undefined => {
  const { startDate, endDate } = value ?? {}
  if (!startDate || !endDate) {
    return i18n.t('invalid_timerange', 'Ungültiger Zeitraum')
  }

  return undefined
}

export const mustBeISOString = (value: string): string | undefined => {
  return !value ||
    /^(-?(?:[1-9][0-9]*)?[0-9]{4})-(1[0-2]|0[1-9])-(3[01]|0[1-9]|[12][0-9])T(2[0-3]|[01][0-9]):([0-5][0-9]):([0-5][0-9])(.[0-9]+)?(Z)?$/.test(
      value,
    )
    ? undefined
    : i18n.t('invalid_dateformat', 'Ungültiges Datumsformat')
}

export function mustBeBeforeOtherDate(comparatorProp: string) {
  return (value: string, allValues: unknown): string | null => {
    const propValue = get(allValues, comparatorProp)
    if (!value || !propValue) {
      return null
    }
    if (moment(value).isSameOrAfter(moment(propValue))) {
      return i18n.t('must_be_before_end_date', 'Muss vor dem Enddatum liegen')
    }
    return null
  }
}

export function mustBeAfterOtherDate(comparatorProp: string) {
  return (value: string, allValues: unknown): string | null => {
    const propValue = get(allValues, comparatorProp)
    if (!value || !propValue) {
      return null
    }
    if (moment(value).isSameOrBefore(moment(propValue))) {
      return i18n.t(
        'must_be_after_start_date',
        'Muss nach dem Startdatum liegen',
      )
    }
    return null
  }
}
